import React, { useEffect, useState } from "react";
import { Box, Heading, HStack, Text, Center, Spinner, Input, Table, Thead, Tbody, Tr, Th, Td, Button, ButtonGroup } from "@chakra-ui/react";
import { saApi } from "../../api/superAdminApi";

const isoStartOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x.toISOString(); };
const isoEndOfDay = (d) => { const x = new Date(d); x.setHours(23,59,59,999); return x.toISOString(); };

const getCurrentMonthBounds = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  return { from: start, to: end };
};

const SKILL_TYPES = [
  { value: "technical", label: "Technical" },
  { value: "functional", label: "Functional" },
  { value: "business", label: "Business" },
];

const Statistics = () => {
  const initial = getCurrentMonthBounds();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [skillType, setSkillType] = useState("technical");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [companies, setCompanies] = useState([]);
  const [topSkills, setTopSkills] = useState([]);

  const totalCv = companies.reduce((sum, c) => sum + Number(c?.cv_count || 0), 0);
  const totalTests = companies.reduce((sum, c) => sum + Number(c?.tests_count || 0), 0);
  const totalSearches = companies.reduce(
    (sum, c) => sum + Number(c?.searches_run || 0),
    0
  );
  const totalProfilesDelivered = companies.reduce(
    (sum, c) => sum + Number(c?.profiles_delivered || 0),
    0
  );

  useEffect(() => {
    const load = async () => {
      if (!from || !to) return;
      if (new Date(from) > new Date(to)) {
        setError("Invalid date range");
        return;
      }
      setIsLoading(true);
      setError("");
      try {
        const res = await saApi.getMetrics({
          dt_from: isoStartOfDay(from),
          dt_to: isoEndOfDay(to),
          skill_type: skillType,
        });
        setCompanies(Array.isArray(res?.companies) ? res.companies : []);
        setTopSkills(Array.isArray(res?.top_skills) ? res.top_skills : []);
      } catch (e) {
        setError("Failed to load metrics");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [from, to, skillType]);

  const maxCount = topSkills.length > 0 ? topSkills[0].evaluation_count : 1;

  return (
    <Box>
      <Heading size="md" mb={4}>Statistics</Heading>

      <Box bg="white" borderWidth="1px" borderRadius="lg" p={4} mb={4}>
        <HStack spacing={4} align="flex-end" justify="space-between">
          <HStack spacing={4} align="flex-end">
            <Box>
              <Text fontSize="xs" mb={1}>From</Text>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} size="sm" />
            </Box>
            <Box>
              <Text fontSize="xs" mb={1}>To</Text>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} size="sm" />
            </Box>
            {error && <Text color="red.500" fontSize="sm">{error}</Text>}
          </HStack>
          <HStack spacing={6}>
            <Text fontSize="md" color="gray.800">CVs: <Text as="span" fontWeight="bold">{totalCv}</Text></Text>
            <Text fontSize="md" color="gray.800">Tests: <Text as="span" fontWeight="bold">{totalTests}</Text></Text>
            <Text fontSize="md" color="gray.800">Searches: <Text as="span" fontWeight="bold">{totalSearches}</Text></Text>
            <Text fontSize="md" color="gray.800">Profiles delivered: <Text as="span" fontWeight="bold">{totalProfilesDelivered}</Text></Text>
          </HStack>
        </HStack>
      </Box>

      {isLoading ? (
        <Box borderWidth="1px" borderRadius="lg" bg="white" p={6}>
          <Center py={10}><Spinner /></Center>
        </Box>
      ) : error ? (
        <Box color="red.500">{error}</Box>
      ) : (
        <>
          <Box borderWidth="1px" borderRadius="lg" bg="white" p={0} overflowX="auto" mb={6}>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Company</Th>
                  <Th>Code</Th>
                  <Th isNumeric>Users (all-time)</Th>
                  <Th isNumeric>CVs</Th>
                  <Th isNumeric>Tests</Th>
                  <Th isNumeric>Searches</Th>
                  <Th isNumeric>Profiles delivered</Th>
                </Tr>
              </Thead>
              <Tbody>
                {companies.length === 0 ? (
                  <Tr>
                    <Td colSpan={7}>
                      <Text fontSize="sm" color="gray.600">No data for selected range.</Text>
                    </Td>
                  </Tr>
                ) : (
                  companies.map((c) => (
                    <Tr key={c.company_id} data-testid={`metrics-row-${c.company_id}`}>
                      <Td>{c.company_name}</Td>
                      <Td>{c.company_code}</Td>
                      <Td isNumeric>{c.user_count ?? 0}</Td>
                      <Td isNumeric>{c.cv_count ?? 0}</Td>
                      <Td isNumeric>{c.tests_count ?? 0}</Td>
                      <Td isNumeric data-testid={`metrics-searches-${c.company_id}`}>
                        {c.searches_run ?? 0}
                      </Td>
                      <Td isNumeric data-testid={`metrics-profiles-delivered-${c.company_id}`}>
                        {c.profiles_delivered ?? 0}
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>

          <Box bg="white" borderWidth="1px" borderRadius="lg" p={5}>
            <HStack justify="space-between" align="center" mb={4}>
              <Heading size="sm">Top 10 Skills Requested for Evaluation</Heading>
              <ButtonGroup size="sm" isAttached variant="outline">
                {SKILL_TYPES.map((st) => (
                  <Button
                    key={st.value}
                    onClick={() => setSkillType(st.value)}
                    colorScheme={skillType === st.value ? "blue" : "gray"}
                    variant={skillType === st.value ? "solid" : "outline"}
                  >
                    {st.label}
                  </Button>
                ))}
              </ButtonGroup>
            </HStack>

            {topSkills.length === 0 ? (
              <Text fontSize="sm" color="gray.500">No skill data for the selected range and type.</Text>
            ) : (
              <Box>
                {topSkills.map((skill, index) => (
                  <HStack key={`${skill.technology}-${index}`} spacing={3} mb={2} align="center">
                    <Text fontSize="sm" color="gray.500" w="20px" textAlign="right" flexShrink={0}>
                      {index + 1}
                    </Text>
                    <Text fontSize="sm" fontWeight="medium" w="160px" flexShrink={0} noOfLines={1}>
                      {skill.technology}
                    </Text>
                    <Box flex="1" bg="gray.100" borderRadius="full" h="8px" overflow="hidden">
                      <Box
                        bg="blue.400"
                        h="100%"
                        borderRadius="full"
                        w={`${Math.round((skill.evaluation_count / maxCount) * 100)}%`}
                        transition="width 0.3s ease"
                      />
                    </Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700" w="32px" textAlign="right" flexShrink={0}>
                      {skill.evaluation_count}
                    </Text>
                  </HStack>
                ))}
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default Statistics;


